import type {
  ApplicationDeviceRiskResponseDto,
  ApplicationJourneyExecutionDto,
  ApplicationJourneyHeaderDto,
  ApplicationJourneyResponseDto,
  ApplicationVerificationSnapshotErrorDto,
} from '../api/admin';
import {
  hasMilestone as hasEmploymentMilestone,
  isEmploymentForwardedUnderReview,
  resolveEmploymentAdminPhase,
} from './employment/employmentJourneyStatus';
import { employerTabStatusFromFootprint } from './employerFootprintCopy';
import {
  legacyLinkageBlocksFunding,
  normalizeLinkageStatus,
} from './legacyLinkageDisplay';
import { isEmploymentPendingManualReviewHold } from './manualReviewHold';

export type ApplicationTabKey =
  | 'overview'
  | 'device'
  | 'identity'
  | 'income'
  | 'decision'
  | 'employer'
  | 'loan-review'
  | 'manual-review'
  | 'data-notes';

export type ApplicationTabStatus = 'pending' | 'pass' | 'review' | 'fail' | 'neutral';

export const APPLICATION_DETAIL_TAB_ORDER: ReadonlyArray<{
  key: ApplicationTabKey;
  label: string;
}> = [
  { key: 'overview', label: 'Overview' },
  { key: 'device', label: 'Device intelligence' },
  { key: 'identity', label: 'Identity' },
  { key: 'income', label: 'Income' },
  // Post-income capacity DE sits between income and employer in the journey.
  { key: 'decision', label: 'Decision engine' },
  { key: 'employer', label: 'Employer' },
  { key: 'loan-review', label: 'Loan review' },
  { key: 'manual-review', label: 'Manual review' },
  // Last: it is where a reviewer goes to act, not part of the journey narrative
  // the earlier tabs tell.
  { key: 'data-notes', label: 'Data & notes' },
] as const;

export const TAB_QUERY_BY_INDEX: Record<number, ApplicationTabKey> = {
  0: 'overview',
  1: 'device',
  2: 'identity',
  3: 'income',
  4: 'decision',
  5: 'employer',
  6: 'loan-review',
  7: 'manual-review',
  8: 'data-notes',
};

export const TAB_INDEX_FROM_QUERY: Record<string, number> = {
  overview: 0,
  device: 1,
  // Legacy Account access tab — fold into Device intelligence.
  onboarding: 1,
  'account-access': 1,
  accountaccess: 1,
  identity: 2,
  income: 3,
  decision: 4,
  employer: 5,
  'loan-review': 6,
  loanreview: 6,
  'manual-review': 7,
  manualreview: 7,
  'data-notes': 8,
  datanotes: 8,
  notes: 8,
  changes: 8,
};

function readListItems(payload: unknown): Record<string, unknown>[] {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return [];
  const raw = (payload as Record<string, unknown>).items;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (x): x is Record<string, unknown> => x != null && typeof x === 'object' && !Array.isArray(x),
  );
}

function recordStatusFromItems(items: Record<string, unknown>[]): ApplicationTabStatus | null {
  if (items.length === 0) return null;
  let hasReview = false;
  for (const row of items) {
    const status = String(row.finalStatus ?? row.status ?? row.verdict ?? '').toUpperCase();
    if (/FAIL|REJECT|DECLIN|DENIED|ERROR/.test(status)) return 'fail';
    if (/REVIEW|PENDING|CHALLENGE|WARN/.test(status)) hasReview = true;
  }
  if (hasReview) return 'review';
  return 'pass';
}

/** Map a single identity session row to a tab status. */
function identityStatusFromSessionRow(row: Record<string, unknown>): ApplicationTabStatus | null {
  const status = String(
    row.finalStatus ?? row.status ?? row.session_status ?? row.verdict ?? '',
  ).toUpperCase();
  if (!status) return null;
  if (/FAIL|REJECT|DECLIN|DENIED|ERROR/.test(status)) return 'fail';
  if (/REVIEW|PENDING|CHALLENGE|WARN|LINK|PROGRESS|PROCESSING/.test(status)) return 'review';
  if (/SUCCESS|COMPLETED|PASS|APPROV|CLEAR|IDV_COMPLETED/.test(status)) {
    return identitySessionHasPersistedEvidence(row) ? 'pass' : 'review';
  }
  return null;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function readDocumentType(record: Record<string, unknown> | null): boolean {
  if (!record) return false;
  return nonEmptyString(record.document_type ?? record.documentType);
}

/** Approved identity must have document_type — same bar as loan review G01. */
export function identitySessionHasPersistedEvidence(row: unknown): boolean {
  const record = readRecord(row);
  if (!record) return false;
  if (readDocumentType(record)) return true;

  const ocr = readRecord(record.ocr_data ?? record.ocrData);
  if (readDocumentType(ocr)) return true;

  const preOcr = readRecord(record.pre_ocr_data ?? record.preOcrData);
  if (readDocumentType(preOcr)) return true;

  const vendorOcr = readRecord(record.vendor_ocr_data ?? record.vendorOcrData);
  if (readDocumentType(vendorOcr)) return true;

  const customer = readRecord(record.customer);
  const customerOcr = readRecord(customer?.ocr_data ?? customer?.ocrData);
  if (readDocumentType(customerOcr)) return true;

  return false;
}

/**
 * Identity tab follows the latest attempt — earlier OCR failures must not keep the
 * tab red after a later attempt passes or lands in review.
 */
function latestIdentitySessionStatus(items: Record<string, unknown>[]): ApplicationTabStatus | null {
  if (items.length === 0) return null;
  const sorted = [...items].sort((a, b) => {
    const aAttempt =
      typeof a.verification_attempt_number === 'number' ? a.verification_attempt_number : 0;
    const bAttempt =
      typeof b.verification_attempt_number === 'number' ? b.verification_attempt_number : 0;
    if (aAttempt !== bAttempt) return aAttempt - bAttempt;
    const aTime = new Date(String(a.created_at ?? a.updated_at ?? 0)).getTime();
    const bTime = new Date(String(b.created_at ?? b.updated_at ?? 0)).getTime();
    return (Number.isFinite(aTime) ? aTime : 0) - (Number.isFinite(bTime) ? bTime : 0);
  });
  return identityStatusFromSessionRow(sorted[sorted.length - 1]!);
}

function hasMilestone(milestones: string[] | undefined, code: string): boolean {
  return (milestones ?? []).includes(code);
}

function scopeError(
  snapshotErrors: ApplicationVerificationSnapshotErrorDto[],
  scope: ApplicationVerificationSnapshotErrorDto['scope'],
): boolean {
  return snapshotErrors.some((e) => e.scope === scope);
}

function contactSignalsNeedReview(device: ApplicationDeviceRiskResponseDto | null | undefined): boolean {
  if (!device?.onboarding) return false;
  const { contactComparison: c, contactEvents, otpAttempts } = device.onboarding;
  const otpFailed = (otpAttempts?.channels ?? []).some(
    (ch) => ch.verifyFailed > 0 || ch.lockedSessions > 0,
  );
  const contactMismatch =
    c.attemptedEmailMatchesOnFile === false ||
    c.attemptedPhoneMatchesOnFile === false ||
    c.otpEmailMatchesOnFile === false ||
    c.otpEmailMatchesAttempted === false ||
    c.otpPhoneMatchesOnFile === false ||
    c.otpPhoneMatchesAttempted === false;
  const flaggedEvents = (contactEvents ?? []).some((e) => e.needsReview);
  const inventoryReview = [
    ...(device.onboarding.visitorContactInventory?.emails ?? []),
    ...(device.onboarding.visitorContactInventory?.phones ?? []),
  ].some((row) => row.needsReview);
  return otpFailed || contactMismatch || flaggedEvents || inventoryReview;
}

function deviceTabStatus(device: ApplicationDeviceRiskResponseDto | null | undefined): ApplicationTabStatus {
  if (!device) return 'neutral';
  const level = device.riskVerdict?.level;
  if (level === 'blocked') return 'fail';
  if (level === 'review' || contactSignalsNeedReview(device)) return 'review';
  if (device.assessments.length === 0 && !device.visitorProfile) return 'pending';
  // Observe = incomplete / agent-down / enforcement off — not a green "pass".
  if (level === 'observe') return 'review';
  if (level === 'clear') return 'pass';
  return 'neutral';
}

function identityTabStatus(
  application: ApplicationJourneyHeaderDto,
  snapshotErrors: ApplicationVerificationSnapshotErrorDto[],
  identitySessions: unknown | null,
): ApplicationTabStatus {
  if (scopeError(snapshotErrors, 'identity')) return 'review';
  const linkage = application.legacyLinkage;
  const linkageStatus = normalizeLinkageStatus(linkage);
  if (linkageStatus === 'PENDING_REVIEW' && legacyLinkageBlocksFunding(linkage)) return 'review';
  if (linkageStatus === 'UNAVAILABLE') return 'review';
  if (linkageStatus === 'UNRESOLVED' && legacyLinkageBlocksFunding(linkage)) return 'review';
  const items = readListItems(identitySessions);
  const fromLatest = latestIdentitySessionStatus(items);
  if (fromLatest) return fromLatest;
  if (!hasMilestone(application.completedMilestones, 'IDENTITY_VERIFIED')) return 'pending';
  return items.length > 0 && identitySessionHasPersistedEvidence(items[items.length - 1]!)
    ? 'pass'
    : 'review';
}

function incomeTabStatus(
  application: ApplicationJourneyHeaderDto,
  snapshotErrors: ApplicationVerificationSnapshotErrorDto[],
  incomeRecords: unknown | null,
): ApplicationTabStatus {
  if (scopeError(snapshotErrors, 'income')) return 'review';
  const items = readListItems(incomeRecords);
  const fromRecords = recordStatusFromItems(items);
  if (fromRecords) return fromRecords;
  if (!hasMilestone(application.completedMilestones, 'INCOME_VERIFIED')) return 'pending';
  return items.length > 0 ? 'pass' : 'pending';
}

function employerTabStatus(
  application: ApplicationJourneyHeaderDto,
  snapshotErrors: ApplicationVerificationSnapshotErrorDto[],
  employerRecords: unknown | null,
  employerFootprintStatus?: string | null,
): ApplicationTabStatus {
  if (scopeError(snapshotErrors, 'employer')) return 'review';
  const items = readListItems(employerRecords);
  const fromRecords = recordStatusFromItems(items);
  if (fromRecords === 'fail') return 'fail';
  if (hasEmploymentMilestone(application.completedMilestones, 'EMPLOYMENT_VERIFIED')) {
    return fromRecords ?? (items.length > 0 ? 'pass' : 'pending');
  }
  if (
    hasEmploymentMilestone(application.completedMilestones, 'EMPLOYMENT_PENDING_REVIEW') ||
    isEmploymentForwardedUnderReview({
      milestones: application.completedMilestones,
      lastActionResult: application.lastActionResult,
    })
  ) {
    return 'review';
  }
  if (fromRecords) return fromRecords;
  const phase = resolveEmploymentAdminPhase({
    milestones: application.completedMilestones,
    applicationStatus: application.status,
    lastAction: application.lastAction,
    lastActionResult: application.lastActionResult,
    latestFinalStatus:
      typeof items[0]?.finalStatus === 'string' ? String(items[0]?.finalStatus) : null,
  });
  if (phase === 'retryable_review') return 'review';
  if (phase === 'rejected') return 'fail';
  // Income may kick off digital footprint before formal employer verification records exist.
  const fromFootprint = employerTabStatusFromFootprint(employerFootprintStatus);
  if (fromFootprint) return fromFootprint;
  return 'pending';
}

function decisionEngineTabStatus(
  decisionEngine: ApplicationJourneyHeaderDto['decisionEngine'] | null | undefined,
): ApplicationTabStatus | null {
  if (!decisionEngine?.evaluationId && !decisionEngine?.decision) return null;
  const decision = String(decisionEngine.decision ?? '').toUpperCase();
  if (/DECLIN/.test(decision)) return 'fail';
  if (/REVIEW|CHALLENGE/.test(decision)) return 'review';
  if (/APPROV/.test(decision)) return 'pass';
  // evaluation present without a crisp decision still means DE ran
  return 'pass';
}

function doctrineExecutionsTabStatus(
  executions: ApplicationJourneyExecutionDto[],
): ApplicationTabStatus {
  if (executions.length === 0) return 'pending';
  let hasFail = false;
  let hasReview = false;
  for (const ex of executions) {
    if (ex.status === 'FAILED' || ex.status === 'CANCELLED') hasFail = true;
    const outcome = ex.outcome?.toUpperCase();
    if (outcome === 'FAIL') hasFail = true;
    if (outcome === 'REVIEW' || outcome === 'CHALLENGE') hasReview = true;
  }
  if (hasFail) return 'fail';
  if (hasReview) return 'review';
  const latest = executions[0];
  if (latest?.outcome === 'PASS' || latest?.status === 'COMPLETED') return 'pass';
  return 'neutral';
}

/** Prefer post-income svc-de snapshot; fall back to doctrine execution rollups. */
function decisionTabStatus(
  executions: ApplicationJourneyExecutionDto[],
  decisionEngine: ApplicationJourneyHeaderDto['decisionEngine'] | null | undefined,
): ApplicationTabStatus {
  return decisionEngineTabStatus(decisionEngine) ?? doctrineExecutionsTabStatus(executions);
}

function overviewTabStatus(application: ApplicationJourneyHeaderDto): ApplicationTabStatus {
  const status = application.status.trim().toUpperCase();
  if (status === 'DECLINED' || status === 'WITHDRAWN') return 'fail';
  if (status === 'FUNDED' || status === 'APPROVED') return 'pass';
  const lr = application.loanReview;
  if (lr?.requestStatus === 'PENDING') return 'review';
  if (lr?.decision === 'REVIEW' || lr?.decision === 'DECLINE') return 'review';
  if ((application.completedMilestones ?? []).includes('EMPLOYMENT_PENDING_REVIEW')) return 'review';
  return 'neutral';
}

function loanReviewTabStatus(
  loanReview: ApplicationJourneyHeaderDto['loanReview'] | undefined,
  applicationStatus?: string,
): ApplicationTabStatus {
  if (!loanReview?.requestStatus) return 'pending';
  if (loanReview.requestStatus === 'PENDING') return 'neutral';
  if (loanReview.requestStatus === 'FAILED') return 'fail';
  if (loanReview.decision === 'APPROVE') {
    // Agent may propose APPROVE while orchestrator parks the loan for human review
    // (e.g. income REVIEW / multiple CLASS_A still uncleared).
    if (String(applicationStatus ?? '').trim().toUpperCase() === 'MANUAL_REVIEW') {
      return 'review';
    }
    return 'pass';
  }
  if (loanReview.decision === 'REVIEW' || loanReview.decision === 'DECLINE') return 'review';
  return 'neutral';
}

/**
 * Compact accept/reject audit on the Decision engine tab. While status is
 * MANUAL_REVIEW the full reviewer cockpit lives on the Manual review tab instead.
 */
export function shouldShowEmployeeDecisionRecordOnDecisionTab(
  applicationStatus: string | undefined | null,
): boolean {
  return String(applicationStatus ?? '').trim().toUpperCase() !== 'MANUAL_REVIEW';
}

/**
 * Manual review tab: only relevant while a loan is awaiting an employee decision
 * (status MANUAL_REVIEW) or once an employee has decided it. Otherwise 'pending'
 * so the tab stays disabled/hidden — the decision cockpit lives here, separate
 * from the Decision engine tab.
 */
function manualReviewTabStatus(application: ApplicationJourneyHeaderDto): ApplicationTabStatus {
  const status = application.status.trim().toUpperCase();
  if (status === 'WITHDRAWN' || status === 'DECLINED') return 'fail';
  if (status === 'MANUAL_REVIEW') return 'review';
  const ed = application.loanReview?.employeeDecision;
  if (ed) return ed.decision === 'ACCEPT' ? 'pass' : 'fail';
  return 'pending';
}

export function computeApplicationTabStatuses(input: {
  journey: ApplicationJourneyResponseDto;
  deviceRisk?: ApplicationDeviceRiskResponseDto | null;
  /** Salary digital footprint status from EV admin GET (PENDING/RUNNING/READY/FAILED unlocks Employer). */
  employerFootprintStatus?: string | null;
}): Record<ApplicationTabKey, ApplicationTabStatus> {
  const { application, executions, verification } = input.journey;
  const milestones = application.completedMilestones ?? [];
  const statusUpper = application.status.toUpperCase();
  const appDeclined = statusUpper === 'DECLINED';
  const appWithdrawn = statusUpper === 'WITHDRAWN';

  const statuses: Record<ApplicationTabKey, ApplicationTabStatus> = {
    overview: overviewTabStatus(application),
    device: deviceTabStatus(input.deviceRisk),
    // Always neutral: the data-and-notes tab reports no journey outcome, and it is
    // never gated — a reviewer must be able to record a correction or a note at
    // any stage, including the terminal ones where every other tab is read-only.
    'data-notes': 'neutral',
    identity: identityTabStatus(application, verification.snapshotErrors, verification.identitySessions),
    income: incomeTabStatus(application, verification.snapshotErrors, verification.incomeRecords),
    decision: decisionTabStatus(executions, application.decisionEngine),
    employer: employerTabStatus(
      application,
      verification.snapshotErrors,
      verification.employerRecords,
      input.employerFootprintStatus,
    ),
    'loan-review': loanReviewTabStatus(application.loanReview, application.status),
    'manual-review': manualReviewTabStatus(application),
  };

  if (appDeclined || appWithdrawn) {
    // Keep Decision / Employer / Loan review open so operators can see why later steps never ran.
    // Do not strip real verification outcomes (pass/fail/review) — Income Passed stays green even when the loan is DECLINED.
    if (statuses.decision === 'pending') statuses.decision = 'neutral';
    if (statuses['loan-review'] === 'pending') statuses['loan-review'] = 'neutral';
    const employmentStarted =
      hasEmploymentMilestone(milestones, 'EMPLOYMENT_VERIFIED') ||
      hasEmploymentMilestone(milestones, 'EMPLOYMENT_PENDING_REVIEW') ||
      readListItems(verification.employerRecords).length > 0 ||
      employerTabStatusFromFootprint(input.employerFootprintStatus) != null;
    if (!employmentStarted && (statuses.employer === 'pending' || statuses.employer === 'fail')) {
      statuses.employer = 'neutral';
    } else if (statuses.employer === 'pending') {
      statuses.employer = 'neutral';
    }
  }

  if (!hasMilestone(milestones, 'IDENTITY_VERIFIED')) {
    if (statuses.income === 'pass') statuses.income = 'pending';
    if (statuses.decision === 'pass' && !application.decisionEngine?.evaluationId) {
      statuses.decision = 'pending';
    }
    if (statuses.employer === 'pass') statuses.employer = 'pending';
  }

  return statuses;
}

export function tabStatusLabel(
  status: ApplicationTabStatus,
  context?: {
    tabKey?: ApplicationTabKey;
    application?: ApplicationJourneyHeaderDto;
  },
): string {
  if (
    context?.tabKey === 'manual-review' &&
    status === 'review' &&
    context.application &&
    isEmploymentPendingManualReviewHold(context.application)
  ) {
    return 'Awaiting clearance';
  }
  switch (status) {
    case 'pending':
      return 'Not started';
    case 'pass':
      return 'Complete';
    case 'review':
      return 'Needs review';
    case 'fail':
      return 'Failed';
    default:
      return '';
  }
}

/** Tabs that have not started yet cannot be opened. Overview + device stay available. */
export function isApplicationTabEnabled(
  tabKey: ApplicationTabKey,
  status: ApplicationTabStatus,
): boolean {
  if (tabKey === 'overview' || tabKey === 'device' || tabKey === 'data-notes') return true;
  return status !== 'pending';
}

export function firstEnabledTabIndex(
  tabStatuses: Record<ApplicationTabKey, ApplicationTabStatus>,
): number {
  const idx = APPLICATION_DETAIL_TAB_ORDER.findIndex(({ key }) =>
    isApplicationTabEnabled(key, tabStatuses[key]),
  );
  return idx >= 0 ? idx : 0;
}
